/** biome-ignore-all lint/a11y/noStaticElementInteractions: We want a clickable wrapper around the combobox without a11y issues */
import { useStoreState } from '@ariakit/react';
import { Combobox, useComboboxStore } from '@ariakit/react/combobox';
import cn from 'classnames';
import { useDeferredValue, useEffect, useMemo, useRef } from 'react';
import { useController, type FieldPathByValue, type UseControllerProps } from 'react-hook-form';
import { useMaxTagNamesQuery } from '#api/tags.queries.ts';
import { HelpText, Label } from '#components/atoms/forms/index.ts';
import { TagPillRemovable } from '#components/atoms/pill/tag-pill/TagPillRemovable.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectMenu } from '#components/patterns/forms/select/select-menu/SelectMenu.tsx';
import { useGuaranteedId } from '#hooks/useGuaranteedId.ts';
import { tagNameSchema, type TagName } from '#schemas/tag/name.ts';
import { useMergedRefs } from '#util/mergeRefs.ts';
import { ComboboxItem } from '../combobox-item/ComboboxItem.tsx';
import { matchTags } from '../match-tags.ts';
import styles from './ComboboxTags.module.css';

/** How {@link ComboboxTags} treats a typed tag that isn't an existing host tag. */
export type UnknownTagsMode = 'reference' | 'create';

type Props<T extends { tags: string[] }, N extends FieldPathByValue<T, string[]>> = UseControllerProps<T, N> & {
  className?: string;
  /**
   * How to handle a typed tag that isn't an existing host tag:
   * - 'reference': accept it as-is (added neutrally) without creating a tag — for referencing a tag
   *   no host wears yet.
   * - 'create' (default): offer to create it (rendered green); host forms materialize it on save.
   */
  unknownTags?: UnknownTagsMode;
  helpText?: string;
  hasError?: boolean;
  label?: string;
  /** Extra classes for the label, e.g. 'visually-hidden' when the surrounding UI already names the field */
  labelClassName?: string;
  /** Focus the input on mount, e.g. when the component appears in response to an edit action */
  focusOnMount?: boolean;
};

/**
 * A react-hook-form controlled component to show an editable list of tags.
 * The user can type into the input to filter a list of available tags (fetched from the server),
 * and select them.
 */
export function ComboboxTags<T extends { tags: string[] }, N extends FieldPathByValue<T, string[]>>({
  className,
  name,
  control,
  unknownTags = 'create',
  hasError,
  helpText,
  label = 'Tags',
  labelClassName,
  focusOnMount,
  disabled,
}: Props<T, N>) {
  const id = useGuaranteedId({ prefix: 'tag' });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const allTags = useMaxTagNamesQuery().data.data;
  const combobox = useComboboxStore({
    resetValueOnHide: true,
  });
  const comboboxValue = useStoreState(combobox, 'value');
  const deferredValue = useDeferredValue(comboboxValue);
  const {
    field: { value: tags, onChange, onBlur, ref: formRef },
  } = useController<T>({
    name,
    control,
  });

  if (!(Array.isArray(tags) && tags.every((value) => typeof value === 'string'))) {
    throw new Error('expected `tags` to be `string[]`');
  }

  // How to treat a typed tag that isn't an existing host tag (see the `unknownTags` prop docs).
  const createsNewTags = unknownTags === 'create';

  const disableChanges = Boolean(disabled);

  // Combine ref from react-hook-form with ours
  const ref = useMergedRefs(formRef, inputRef);

  // Focus programmatically rather than with the autoFocus prop: this only ever runs when the
  // component appears in response to a user action, not on page load.
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  // Only set matches if we have less than 15 so far (that's the max)
  const matches = useMemo(
    () =>
      tags.length < 15 ?
        // Dedupe: the tag list can contain the same name twice (e.g. an org with a duplicate),
        // which would otherwise render duplicate React keys in the options list.
        [
          ...new Set(
            matchTags(allTags, deferredValue)
              // filter out tags that have already been added
              .filter((t) => !tags.includes(t))
          ),
        ]
      : [],
    [deferredValue, allTags, tags]
  );

  function getAnchorRect() {
    if (!containerRef.current) return null;
    return containerRef.current.getBoundingClientRect();
  }

  function focusInput() {
    ref.current?.focus();
    // Focusing with JS does not open the menu, so manually show it
    combobox.show();
  }

  function addTag(tag: TagName) {
    combobox.setValue('');
    onChange(tags.concat(tag));
  }

  function removeTag(index: number) {
    // This should always be an array, but TS doesn't know that
    if (Array.isArray(tags)) {
      // Hide the popover when removing tags
      combobox.hide();
      // Remove the element at index
      onChange(tags.toSpliced(index, 1));
    }
  }

  /**
   * Potentially create a row for the bottom of the popover menu when the user is trying to create a new tag.
   * We'll either show a validation error message or a `Create <X>` option.
   */
  const makeFallbackOption = () => {
    // If they've already added the tag, don't allow a duplicate
    if (tags.includes(comboboxValue)) {
      return (
        <ComboboxItem key={comboboxValue} value={comboboxValue} setValueOnClick={false} hideOnClick={false} isMessage>
          {`${comboboxValue} is already in list`}
        </ComboboxItem>
      );
    }

    if (tags.length >= 15) {
      return (
        <ComboboxItem key={comboboxValue} value={comboboxValue} setValueOnClick={false} hideOnClick={false} isMessage>
          A maximum of 15 tags may be applied.
        </ComboboxItem>
      );
    }

    // Check if the user is trying to create a new tag, only show if no other matches
    let validationError: string | undefined;
    const result = tagNameSchema.safeParse(comboboxValue);
    if (!result.success) {
      validationError = result.error.flatten().formErrors[0];
    }
    if (comboboxValue && typeof validationError === 'string' && !matches.length) {
      return (
        <ComboboxItem key={comboboxValue} value={comboboxValue} setValueOnClick={false} hideOnClick={false} isMessage>
          {validationError}
        </ComboboxItem>
      );
    }

    // If they've typed a valid tag that isn't already an option, offer to add it. In 'create' mode
    // this creates a new tag; in 'reference' mode it just adds the (possibly not-yet-existing) tag.
    if (result.success && !matches.includes(comboboxValue)) {
      return (
        <ComboboxItem
          key={comboboxValue}
          value={comboboxValue}
          onClick={() => addTag(result.data)}
          setValueOnClick={false}
          hideOnClick={false}
        >
          {createsNewTags ? 'Create' : 'Add'} {comboboxValue}
        </ComboboxItem>
      );
    }

    // Just show the matches
    if (matches.length) {
      return null;
    }

    // No matches and an empty input: prompt the user to start typing a tag.
    // In 'reference' mode we only show this while the field is still empty — otherwise the hint
    // would keep the menu open (covering e.g. a modal's submit button) after the input resets when
    // a tag is added. In 'create' mode we keep the long-standing behavior of always showing it.
    if (!comboboxValue && (createsNewTags || tags.length === 0)) {
      return (
        <ComboboxItem key={comboboxValue} value={comboboxValue} setValueOnClick={false} hideOnClick={false} isMessage>
          Start typing to {createsNewTags ? 'create' : 'add'} a tag
        </ComboboxItem>
      );
    }

    return null;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!comboboxValue && event.key === 'Backspace') {
      removeTag(-1);
    }
  };

  return (
    <Field
      label={
        <Label htmlFor={id} className={labelClassName}>
          {label}
        </Label>
      }
    >
      {helpText && <HelpText>{helpText}</HelpText>}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(
          'border-focusable',
          styles.ComboboxTags,
          { [styles.ComboboxTags___disabled]: disableChanges, 'has-error': hasError },
          className
        )}
        ref={containerRef}
        onClick={focusInput}
      >
        <div className={styles.ComboboxTags_tagList}>
          {tags.map((tag: string, i) => (
            <TagPillRemovable
              className={cn('truncate', styles.ComboboxTags_tagPill)}
              key={tag}
              label={tag}
              disabled={disableChanges}
              onClick={() => removeTag(i)}
              variant={createsNewTags && !allTags.includes(tag) ? 'success' : 'default'}
            />
          ))}
          <Combobox
            store={combobox}
            ref={ref}
            id={id}
            onBlur={onBlur}
            disabled={disableChanges}
            className={styles.ComboboxTags_input}
            placeholder={tags.length || disableChanges ? '' : 'Choose tags…'}
            autoSelect={Boolean(comboboxValue.length)}
            render={(htmlProps) => (
              <input
                {...htmlProps}
                onKeyDown={(e) => {
                  onKeyDown(e);
                  htmlProps.onKeyDown?.(e);
                }}
              />
            )}
          />
        </div>
        {!disableChanges && (matches.length || makeFallbackOption()) ?
          <SelectMenu
            className={styles.ComboboxTags_menu}
            type="combobox"
            store={combobox}
            getAnchorRect={getAnchorRect}
            noItemsMessage="No tags found"
            gutter={4}
            disabled={disableChanges}
          >
            {matches.map((val) => (
              <ComboboxItem
                key={val}
                value={val}
                onClick={() => addTag(val)}
                setValueOnClick={false}
                hideOnClick={false}
              >
                {val}
              </ComboboxItem>
            ))}
            {makeFallbackOption()}
          </SelectMenu>
        : null}
      </div>
    </Field>
  );
}
